import { RestError } from '@azure/core-http';
import { CourierListItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Container } from '@mui/material';
import { useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { createPath, Paths, ProjectPathParams } from '../../../app/router/app.router';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { Snackbar } from '../../../ui/components/pure/snackbar/snackbar.component';
import { CompleteRegistrationModal } from '../actions/complete-registration/complete-registration-action.component';
import { DeactivateIncompleteCourierModal } from '../actions/deactivate-incomplete/deactivate-incomplete-courier.modal';
import { IncompleteCouriersTable } from './components/incomplete-couriers-table.component';
import { useIncompleteCouriers } from './hooks/use-incomplete-couriers.hook';
import {
  INCOMPLETE_COURIERS_EMPTY_MESSAGE,
  makeIncompleteCouriersPageHeaderProps
} from './logic/incomplete-couriers-props.map';

export const IncompleteCouriersPage = ({ params }: { params: ProjectPathParams<Paths.incompleteCouriers> }) => {
  const location = useLocation();
  const {
    project,
    networkAbove,
    resolving,
    result: couriers,
    infiniteScroll,
    formData,
    setFormData
  } = useIncompleteCouriers(params.projectID);
  const [completing, setCompleting] = useState<CourierListItemDto | null>(null);
  const [deactivating, setDeactivating] = useState<CourierListItemDto | null>(null);
  const [succeeded, setSucceeded] = useState<string | null>(null);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );
  const error = project.error || couriers.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  if (networkAbove) {
    return (
      <Redirect
        to={{
          pathname: createPath(Paths.incompleteCouriers, { projectID: networkAbove.shortcode || networkAbove.id }),
          search: location.search
        }}
      />
    );
  }

  const headerProps = makeIncompleteCouriersPageHeaderProps(
    couriers.data?.pages[0].totalRecords || 0,
    formData,
    setFormData
  );
  return (
    <Container>
      {project.data && <WindowTitle detail={project.data.name} pageTitle="Incomplete Couriers" />}
      <ListPageHeader {...headerProps} />
      <IncompleteCouriersTable
        params={params}
        pages={couriers.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={resolving || couriers.isLoading}
        isPreviousData={couriers.isPreviousData}
        hasError={couriers.isError}
        customMessage={INCOMPLETE_COURIERS_EMPTY_MESSAGE}
        onCompleteRegistration={setCompleting}
        onDeactivate={setDeactivating}
      />
      {completing && (
        <CompleteRegistrationModal
          courier={completing}
          onSuccess={() => {
            setCompleting(null);
            setSucceeded('Registration completed. The courier is now pending approval');
          }}
          onAbort={() => setCompleting(null)}
        />
      )}
      {deactivating && (
        <DeactivateIncompleteCourierModal
          courier={deactivating}
          onSuccess={() => {
            setDeactivating(null);
            setSucceeded('The courier has been deactivated');
          }}
          onCancel={() => setDeactivating(null)}
        />
      )}
      {succeeded && (
        <Snackbar severity="success" onClose={() => setSucceeded(null)}>
          {succeeded}
        </Snackbar>
      )}
    </Container>
  );
};
